import { memo, useState, useRef, forwardRef, useEffect, useMemo } from "react";

import { useReactToPrint } from "react-to-print";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { Chart as ChartJS } from "chart.js";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { Panel } from "primereact/panel";
import { Dropdown } from 'primereact/dropdown';

import { useReport } from "../../context/report";

import { MetragemDiaTurmas } from "./MetragemDiaTurmas";
import { MetragemDiaGrupos } from "./MetragemDiaGrupos";

import ChartDataLabels from "chartjs-plugin-datalabels";
import annotationPlugin from "chartjs-plugin-annotation";

export default memo(function Metragem() {
  const { data, printing, handlePrint } = useReport();

  const [value, setValue] = useState(0);
  const [total, setTotal] = useState(0);
  
  const [selectedPercent, setSelectedPercent] = useState(90);

  const componentRef = useRef(null);

  ChartJS.register(annotationPlugin);

  // Gera as opções do Dropdown (de 100% até 70%)
  const percentOptions = useMemo(() => {
    let options = [];
    for (let i = 100; i >= 70; i--) {
      options.push({ label: `${i}%`, value: i });
    }
    return options;
  }, []);

  const pageStyle = `
    @media print {
        .page-break {
          display: block;
          line-height: 105%;
        }
      }
    `;

  const handlePrintTable = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: pageStyle,
  });

  const options = {
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: `Gráfico de Metragem - ${data.periodo}`,
        font: {
          size: 20,
        },
      },
      subtitle: {
        display: true,
        text: `Total - ${Intl.NumberFormat("pt-BR").format(total)}m | Meta Turno: ${Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(value)}m`,
        font: {
          size: 20,
        },
      },
      legend: {
        labels: {
          color: "#000"
        }
      },
      datalabels: {
        formatter: (value, ctx) => {
          if (ctx.dataset.label === "Meta") {
            return "";
          }
          return Intl.NumberFormat("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value);
        },
        font: {
          size: printing ? 20 : 24,
        },
        color: "#000",
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 16
          },
          color: "#000"
        }
      },
    }
  };

  const ChartMetragem = () => {
    let labels = [];
    let values = [];
    let colors = [];
    let metas = [];

    data.chart.forEach((element) => {
      labels.push([
        `Turma: ${element.turma}`,
        `Total: ${Intl.NumberFormat("pt-BR").format(element.total)}`,
        `Média: ${Intl.NumberFormat("pt-BR").format(element.media)}`,
        `Dias: ${element.dias}`,
      ]);
      values.push(element.total);
      colors.push(element.color);
      metas.push(value * element.dias);
    });

    const chartData = {
      labels: labels,
      datasets: [
        {
          label: "Produção",
          data: values,
          backgroundColor: colors,
          order: 2,
        },
        {
          label: "Meta",
          data: metas,
          type: "line", 
          backgroundColor: "#2980b9",
          borderColor: "#2980b9",
          order: 1, 
        },
      ],
    };

    return (
      <Chart
        id="chartMetragem2"
        height={printing ? "210mm" : "650px"}
        width={printing ? "297mm" : "100%"}
        type="bar"
        data={chartData}
        plugins={[ChartDataLabels]}
        options={options}
      />
    );
  };

  const TableMetragem = forwardRef((props, ref) => {
    return (
      <div className="page-break" ref={ref}>
        <DataTable
          id="tableMetragem"
          header={() => (
            <div>
              <h2 className="p-m-2">
                Total: {Intl.NumberFormat("pt-BR").format(total)} m
              </h2>
              <h3 className="p-m-2">{data.periodo ? data.periodo : ""}</h3>
            </div>
          )}
          value={data.datatable}
        >
          <Column header="Tear" field="tear"></Column>
          <Column
            header="Por Turma"
            body={(rowData) => (
              <div className="p-d-flex p-flex-column">
                {rowData.turmas.map((element, index) => (
                  <span key={index} className="p-text-bold">
                    {element.label}
                  </span>
                ))}
              </div>
            )}
          ></Column>
          <Column header="Total" field="total"></Column>
        </DataTable>
      </div>
    );
  });

  const ChartGrupos = ({ index, type }) => {
    let labels = [];
    let datasets = [];
    let metas = [];

    data.grupos.forEach((element) => {
      labels.push([
        element.label,
        "Total: " + element.total.toFixed(2),
        "Média: " + element.media.toFixed(2),
        "Dias: " + element.dias,
      ]);
      datasets.push(element.total);
      
      // Como o backend mandou a meta fixada em 90%, fazemos a conversão matemática 
      // baseada no percentual que o usuário selecionou no dropdown
      const meta90 = element.meta_dia || 0;
      const metaCalculada = Math.round(meta90 * (selectedPercent / 90));
      
      metas.push(metaCalculada * element.dias);
    });

    const chartData = {
      labels: labels,
      datasets: [
        {
          label: "Produção",
          data: datasets,
          backgroundColor: "#e67e22",
          order: type === "meta" ? 1 : 2,
        },
        {
          label: "Meta",
          data: metas,
          type: type === "meta" ? "" : "line",
          backgroundColor: "#2980b9",
          borderColor: "#2980b9",
          order: type === "meta" ? 2 : 1,
        },
      ],
    };

    return (
      <Chart
        id={`chartMetragem${index}`}
        height={printing ? "210mm" : "650px"}
        width={printing ? "297mm" : "100%"}
        type="bar"
        options={{
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: "Meta por Turma e Grupos - " + data.periodo,
              font: {
                size: 18,
              },
            },
            subtitle: {
              display: true,
              text:
                type === "meta"
                  ? ""
                  : `Meta baseada na eficiência diária (${selectedPercent}%) por grupo`,
              font: {
                size: 16,
                lineHeight: type === "meta" ? 0 : 2.5,
              },
            },
            legend: {
              labels: {
                color: "#000"
              }
            },
            datalabels: {
              formatter: (label, ctx) => {
                if (ctx.dataset.label === "Meta" && type !== "meta") {
                  return "";
                }
                return Intl.NumberFormat("pt-BR", {
                  maximumFractionDigits: 2,
                }).format(label);
              },
              font: {
                size: printing ? 20 : 24,
              },
              color: "#000",
            },
          },
          scales: {
            x: {
              ticks: {
                font: {
                  size: 16
                },
                color: "#000"
              }
            },
          }
        }}
        data={chartData}
        plugins={[ChartDataLabels]}
      />
    );
  };
  
  useEffect(() => {
    if (data && data.dados && data.dados.datasets) {
      data.dados.datasets.forEach((element) => {
        element.order = 2;
      });
    }

    setTotal(data.total || 0);

    if (data && data.total_turno) { 
      const totalTurnoCalculado = Math.round(data.total_turno * (selectedPercent / 90));
      setValue(totalTurnoCalculado);
    }
  }, [data, selectedPercent]);

  return (
    <div className="p-d-flex p-flex-column">
      <div className="p-d-flex p-ai-center p-m-2">
        <label htmlFor="percent" className="p-mr-2 p-text-bold">Eficiência:</label>
        <Dropdown
          id="percent"
          value={selectedPercent}
          options={percentOptions}
          onChange={(e) => setSelectedPercent(e.value)}
          placeholder="Percentual"
        />
      </div>
      <Panel header="RELATÓRIO DE METRAGEM POR TEAR" toggleable collapsed={true}>
        <Button
          className="p-m-2"
          icon="pi pi-print"
          onClick={handlePrintTable}
        />
        <TableMetragem ref={componentRef} />
      </Panel>
      <Panel header="GRÁFICO DE METRAGEM POR TURMA" toggleable collapsed={true}>
        <div>
          <Button
            className="p-m-2"
            icon="pi pi-print"
            onClick={() => handlePrint("chartMetragem2")}
          />
          <InputNumber
            className="p-m-2"
            onChange={(e) => setValue(e.value)}
            value={value}
          />
          <span>por turno</span>
        </div>
        <ChartMetragem />
      </Panel>
      <Panel header="GRÁFICO DE METRAGEM POR GRUPO DE MÁQUINAS" toggleable collapsed={true}>
        
        {/* Cabeçalho com o Botão e o Dropdown */}
        <div className="p-d-flex p-flex-column p-md-flex-row p-md-ai-center p-jc-between p-mb-3">
          <div>
            <Button
              className="p-m-2"
              icon="pi pi-print"
              onClick={() => handlePrint("chartMetragem3")}
            />
          </div>
        </div>
        
        {/* Caixa resumo das metas por grupo com o percentual aplicado */}
        {data && data.grupos && data.grupos.length > 0 && (
          <div className="p-m-2 p-p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <span className="p-text-bold">Metas Diárias por Grupo ({selectedPercent}% de Eficiência):</span>
            
            <div className="p-d-flex p-flex-wrap p-mt-2">
              {data.grupos.map((grupo, index) => {
                const meta90 = grupo.meta_dia || 0;
                const metaCalculada = Math.round(meta90 * (selectedPercent / 90));
                
                return (
                  <span key={index} className="p-mr-4 p-mb-2">
                    <strong>{grupo.label}:</strong> {Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(metaCalculada)} m
                  </span>
                )
              })}
            </div>
          </div>
        )}
        
        <ChartGrupos index={3} type="none" />
      </Panel>
      <MetragemDiaTurmas 
        data={data}
        name="dados"
        handlePrint={handlePrint} 
        value={value} 
        setValue={setValue}
        printing={printing}
        total={total}
      />

      <MetragemDiaGrupos />
    </div>
  );
});